import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const MAX_LENGTH = 36;
const FINAL_COUNT = 24;

interface UserChannelState {
  links: {
    next: string | null;
    previous: string | null;
  };
  previousDate: string | null;
  nextDate: string | null;
  results: any[];
  loadedFromApi: boolean;
  hasFilteredDates: boolean;
}

export interface UserChannelVideoStateByChannelId {
  [channelId: string]: UserChannelState;
}

const initialState: UserChannelVideoStateByChannelId = {};

const userChannelVideoSlice = createSlice({
  name: 'userChannelVideo',
  initialState,
  reducers: {
    setUserChannelVideosData: (state, action: PayloadAction<{ channelId: string; data: UserChannelState }>) => {
      const { channelId, data } = action.payload;
      state[channelId] = {
        links: { next: null, previous: null },
        previousDate: null,
        nextDate: null,
        results: data.results,
        loadedFromApi: true,
        hasFilteredDates: false
      };
    },

    insertPreviousVideos: (state, action: PayloadAction<{ channelId: string; data: UserChannelState }>) => {
      const { channelId, data } = action.payload;
      const currentState = state[channelId] || initialState;

      // state[channelId] = {
      //   ...currentState,
      //   // links: { ...currentState.links, previous: data.links.previous, next: data.links.previous },
      //   results: [...currentState.results, ...data.results]
      // };
      // Only slice if combinedResults has at least 24 items
      const combinedResults = [...currentState.results, ...data.results];
      const trimmedResults =
        combinedResults.length >= MAX_LENGTH
          ? combinedResults.slice(combinedResults.length - FINAL_COUNT)
          : combinedResults;
      // const trimmedResults =
      // combinedResults.length >= MAX_LENGTH ? combinedResults.slice(0, FINAL_COUNT) : combinedResults;
      console.log('combinedResults', combinedResults.length);
      console.log('trimmedResults', trimmedResults.length);
      state[channelId] = {
        ...currentState,
        links: { ...currentState.links, previous: data.links.previous, next: data.links.previous },
        results: trimmedResults
      };
    },
    insertNextVideos: (state, action: PayloadAction<{ channelId: string; data: UserChannelState }>) => {
      const { channelId, data } = action.payload;
      const currentState = state[channelId] || initialState;

      // state[channelId] = {
      //   ...currentState,
      //   // links: { ...currentState.links, next: data.links.next, previous: data.links.previous },
      //   results: [...data.results, rrentState.results]
      // };
      const combinedResults = [...data.results, ...currentState.results];
      // const trimmedResults =
      //   combinedResults.length >= MAX_LENGTH
      //     ? combinedResults.slice(combinedResults.length - FINAL_COUNT)
      //     : combinedResults;
      const trimmedResults =
        combinedResults.length >= MAX_LENGTH ? combinedResults.slice(0, FINAL_COUNT) : combinedResults;
      state[channelId] = {
        ...currentState,
        results: trimmedResults
      };
    },

    updateUserChannelVideo: (
      state,
      action: PayloadAction<{ channelId: string; videoId: string; updatedData: any }>
    ) => {
      const { channelId, videoId, updatedData } = action.payload;

      if (!state[channelId]) return;

      state[channelId].results = state[channelId].results.map((video) =>
        video.id === videoId ? { ...video, ...updatedData } : video
      );
    },

    removeUserChannelVideo: (state, action: PayloadAction<{ channelId: string; videoId: string }>) => {
      const { channelId, videoId } = action.payload;

      if (!state[channelId]) return;

      state[channelId].results = state[channelId].results.filter((video) => video.id !== videoId);
    },

    addUserChannelVideo: (state, action: PayloadAction<{ channelId: string; video: any }>) => {
      const { channelId, video } = action.payload;

      if (!state[channelId]) {
        state[channelId] = {
          links: { next: null, previous: null },
          previousDate: null,
          nextDate: null,
          results: [],
          loadedFromApi: false,
          hasFilteredDates: false
        };
      }
      state[channelId].results = [video, ...state[channelId].results];
    },
    updateChannelVideoField: (
      state,
      action: PayloadAction<{ channelId: string; videoHashId: string; field: string; value: any }>
    ) => {
      const { channelId, videoHashId, field, value } = action.payload;
      if (state[channelId]) {
        state[channelId].results = state[channelId].results.map((item) => {
          if (item.hash_id === videoHashId) {
            return {
              ...item,
              [field]: value // Update only the specified field
            };
          }
          return item;
        });
      }
    },
    updateVideoTabChannels: (
      state,
      action: PayloadAction<{
        channelId: string;
        videoHashId: string;
        channel: any;
        type: 'add' | 'remove' | 'archived' | 'reshared';
      }>
    ) => {
      const { channelId, videoHashId, channel, type } = action.payload;
      if (state[channelId]) {
        state[channelId].results = state[channelId].results.map((item) => {
          if (item?.hash_id === videoHashId) {
            let updatedChannels = item.channels || [];
            if (type === 'add') {
              // Add the channel if it's not already in the array
              const exists = updatedChannels.some((ch: any) => ch.channel.id === channel.id);
              if (!exists) {
                updatedChannels = [...updatedChannels, { channel, is_archived: false }];
              }
            } else if (type === 'remove') {
              // Remove the channel if it exists in the array
              updatedChannels = updatedChannels.filter((ch: any) => ch.channel.id !== channel.id);
            } else if (type === 'archived') {
              // Update the is_archived field to true for the matching channel
              updatedChannels = updatedChannels.map((ch: any) =>
                ch.channel.id === channel.id ? { ...ch, is_archived: true } : ch
              );
            } else if (type === 'reshared') {
              // Update the is_archived field to false for the matching channel
              updatedChannels = updatedChannels.map((ch: any) =>
                ch.channel.id === channel.id ? { ...ch, is_archived: false } : ch
              );
            }
            return {
              ...item,
              channels: updatedChannels
            };
          }
          return item;
        });
      }
    },
    setHasFilteredDates: (state, action: PayloadAction<{ channelId: string; hasFilteredDates: boolean }>) => {
      const { channelId, hasFilteredDates } = action.payload;
      if (state[channelId]) {
        state[channelId].hasFilteredDates = hasFilteredDates;
      }
    },
    setNextLink: (state, action: PayloadAction<{ channelId: string; nextLink: string | null }>) => {
      const { channelId, nextLink } = action.payload;
      if (state[channelId]) {
        state[channelId].links.next = nextLink;
      }
    },

    setPreviousLink: (state, action: PayloadAction<{ channelId: string; previousLink: string | null }>) => {
      const { channelId, previousLink } = action.payload;
      if (state[channelId]) {
        state[channelId].links.previous = previousLink;
      }
    },

    resetUserChannelState: () => initialState
  }
});

export const {
  setUserChannelVideosData,
  insertNextVideos,
  insertPreviousVideos,
  resetUserChannelState,
  updateUserChannelVideo,
  removeUserChannelVideo,
  addUserChannelVideo,
  updateChannelVideoField,
  updateVideoTabChannels,
  setHasFilteredDates,
  setNextLink,
  setPreviousLink
} = userChannelVideoSlice.actions;

export const UserChannelVideoReducers = userChannelVideoSlice.reducer;
